import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Space } from '@client/components/_ui/layout/Space';
import { createCommonFields, createFilterConfig, FilterBar, useFilterBuilderWithUrl } from '@client/components/filters';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { useMemo } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types/$productTypeId')({
    staticData: {
        ui: {
            title: 'PDS configuration',
            subtitle: 'Manage Product Disclosure Statements for this product type',
        },
        presentation: 'fullPage',
    },
    params: {
        parse({ productTypeId }) {
            return {
                productTypeId: BigInt(productTypeId),
            };
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        void trpcUtils.productType.getProductType
            .ensureData({
                id: params.productTypeId,
            })
            .catch(() => undefined);

        return {
            breadcrumb: {
                async title() {
                    const productType = await trpcUtils.productType.getProductType.ensureData({
                        id: params.productTypeId,
                    });

                    return productType.name;
                },
            },
        };
    },
    component: ConfigurationPdsByProductType,
});

type PdsWhere = NonNullable<RouterInputs['pds']['listPds']['where']>;

// Create typed commonFields for PdsWhere
const commonFields = createCommonFields<PdsWhere>();

function PdsFilters({
    onChange,
}: {
    /**
     * Callback when filters change
     * Receives the Prisma where clause directly
     */
    onChange: (where: PdsWhere) => void;
}) {
    // Create filter configuration
    const filterConfig = useMemo(() => {
        return createFilterConfig<PdsWhere>([
            // Pinned text search field for searching by name
            commonFields.textSearch({
                id: 'search',
                pinned: true,
                operators: ['contains'],
                defaultOperator: 'contains',
                placeholder: 'Search by name...',
                customWhereBuilder(operator, value) {
                    return match(operator)
                        .with('contains', () => {
                            // Try to parse as ID first
                            const idMatch = value.match(/^\d+$/);
                            const id = idMatch ? BigInt(value) : BigInt(0);

                            return {
                                OR: [{ id: { equals: id } }, { name: { contains: value, mode: 'insensitive' } }],
                            } satisfies PdsWhere;
                        })
                        .otherwise(() => null);
                },
            }),

            // ID filter
            commonFields.id({
                id: 'pds-id',
                label: 'ID',
                dbField: 'id',
            }),

            // Name filter
            commonFields.text({
                id: 'name',
                label: 'Name',
                dbField: 'name',
            }),
        ]);
    }, []);

    // Use filter builder hook with URL persistence
    const filterState = useFilterBuilderWithUrl(filterConfig, 'pds-filters', [], onChange);

    return (
        <FilterBar
            config={filterConfig}
            filters={filterState.filters}
            whereClause={filterState.whereClause}
            onAddFilter={filterState.addFilter}
            onUpdateFilter={filterState.updateFilter}
            onRemoveFilter={filterState.removeFilter}
            onClearFilters={filterState.clearFilters}
        />
    );
}

type QueryParams = RouterInputs['pds']['listPds'];
type PdsRow = RouterOutputs['pds']['listPds']['rows'][number];
type PdsOrderBy = NonNullable<QueryParams['orderBy']>;

function ConfigurationPdsByProductType() {
    const orgId = useOrgId();
    const { productTypeId } = Route.useParams();
    const navigate = useNavigate();

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { id: 'desc' },
        where: {},
    });

    const { data, isPending, refetch } = trpc.pds.listPds.useQuery(
        {
            ...queryParams,
            where: {
                ...queryParams.where,
                productTypeId,
            },
        },
        {
            enabled: !!productTypeId,
        },
    );

    const columnHelper = createColumnHelper<PdsRow, PdsOrderBy>();

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ row }) => row.id.toString(),
            width: 80,
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ row }) => <span className="font-medium">{row.name}</span>,
            hideable: false,
        }),
        columnHelper.display({
            id: 'insurer',
            header: 'Insurer',
            cell: ({ row }) => row.Insurer?.name ?? <span className="text-subtle-foreground">-</span>,
        }),
        columnHelper.display({
            id: 'productType',
            header: 'Product type',
            cell: ({ row }) => row.ProductType?.name ?? <span className="text-subtle-foreground">-</span>,
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Space size="small">
                    <Button.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/pds/$pdsId"
                        params={{ orgId, productTypeId: row.productTypeId, pdsId: row.id }}
                        onClick={(e) => e.stopPropagation()}
                        variant="tertiary"
                    >
                        PDS versions
                    </Button.Link>
                    <Button.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/pds-text/$pdsId"
                        params={{ orgId, productTypeId, pdsId: row.id }}
                        onClick={(e) => e.stopPropagation()}
                        variant="tertiary"
                        icon={<FileText size={14} />}
                    >
                        PDS text
                    </Button.Link>
                </Space>
            ),
            width: 280,
        }),
    ];

    const tableProps = usePrismaTable<PdsRow, QueryParams['where'], PdsOrderBy>({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No PDS documents for this product type yet' },
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'pds-configuration-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <TableToolbar
                filters={<PdsFilters onChange={(where) => setQueryParams((prev) => ({ ...prev, where, offset: 0 }))} />}
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/add"
                        params={{ orgId, productTypeId }}
                        variant="primary"
                        label="New PDS"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/product-types/$productTypeId/edit/$pdsId',
                                params: {
                                    orgId,
                                    productTypeId,
                                    pdsId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />

            <Outlet />
        </PageController>
    );
}
